// src/components/admin/tabs/OverviewTab.js
import React, { useContext, useState, useEffect } from "react";
import { Card, Row, Col, Table, Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIndustry,
  faTriangleExclamation,
  faScrewdriverWrench,
  faCircleCheck,
  faToolbox,
  faClock,
  faGaugeHigh,
} from "@fortawesome/free-solid-svg-icons";
import { DashboardContext } from "../../../contexts/AdminContext";
import api from "../../../axiosConfig";

const OverviewTab = () => {
  const { machines = [], loading } = useContext(DashboardContext);

  const [tools, setTools] = useState([]);
  const [finished, setFinished] = useState([]);
  const [toolHistory, setToolHistory] = useState([]);
  const [, setTick] = useState(0);

  const percent = (value, total) =>
    !total ? "0%" : `${((value / total) * 100).toFixed(1)}%`;

  // re-render durasi tiap 30 detik
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api
      .get("/maintenance/tools/list")
      .then((res) => setTools(res.data || []))
      .catch((err) => console.error("Gagal ambil tools:", err));

    api
      .get("/maintenance/history-final")
      .then((res) => setFinished(res.data || []))
      .catch((err) => console.error("Gagal ambil history mesin:", err));

    api
      .get("/maintenance/tools/history")
      .then((res) => {
        const d = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setToolHistory(d);
      })
      .catch((err) => console.error("Gagal ambil history tools:", err));
  }, []);

  /* =========================
     STATUS MESIN
  ========================= */
  const rusak = machines.filter((m) => m.status === "Need Maintenance");
  const perbaikan = machines.filter((m) => m.status === "Under Maintenance");
  const normalCount =
    machines.length - rusak.length - perbaikan.length;

  const isToday = (d) => {
    if (!d) return false;
    const x = new Date(d);
    const now = new Date();
    return x.toDateString() === now.toDateString();
  };

  const selesaiHariIni = finished.filter((h) => isToday(h.end_time)).length;
  const finishedRecent = [...finished]
    .sort((a, b) => new Date(b.end_time || b.created_at) - new Date(a.end_time || a.created_at))
    .slice(0, 12);

  /* =========================
     STATUS TOOLS
  ========================= */
  const toolsRusak = tools.filter(
    (t) => t.tool_status === "BUTUH_DIPERBAIKI" || t.tool_status === "NG",
  );
  const toolsPerbaikan = tools.filter(
    (t) => t.tool_status === "SEDANG_DIPERBAIKI",
  );

  const toolStats = {
    total: tools.length,
    underMaintenance: toolsPerbaikan.length,
    pending: tools.filter((t) => t.tool_status === "BUTUH_DIPERBAIKI").length,
    ng: tools.filter((t) => t.tool_status === "NG").length,
    normal: tools.filter((t) => t.tool_status === "OK").length,
  };

  const toolSelesaiHariIni = toolHistory.filter((h) =>
    isToday(h.end_time),
  ).length;
  const toolHistoryRecent = [...toolHistory]
    .sort(
      (a, b) =>
        new Date(b.end_time || b.start_time) -
        new Date(a.end_time || a.start_time),
    )
    .slice(0, 12);

  const fmtDateTime = (d) =>
    d
      ? new Date(d).toLocaleString("id-ID", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const durasi = (start) => {
    if (!start) return null;
    const m = Math.floor((Date.now() - new Date(start)) / 60000);
    if (m < 0) return null;
    if (m < 60) return `${m} mnt`;
    if (m < 1440) return `${Math.floor(m / 60)}j ${m % 60}m`;
    return `${Math.floor(m / 1440)} hari`;
  };

  const kpis = [
    {
      label: "Total Mesin",
      value: machines.length,
      icon: faIndustry,
      grad: "linear-gradient(135deg,#4f46e5,#7c3aed)",
    },
    {
      label: "Mesin Rusak",
      value: rusak.length,
      icon: faTriangleExclamation,
      grad: "linear-gradient(135deg,#ef4444,#b91c1c)",
    },
    {
      label: "Sedang Perbaikan",
      value: perbaikan.length,
      icon: faScrewdriverWrench,
      grad: "linear-gradient(135deg,#f59e0b,#d97706)",
    },
    {
      label: "Selesai Diperbaiki (Hari Ini)",
      value: selesaiHariIni,
      icon: faCircleCheck,
      grad: "linear-gradient(135deg,#10b981,#059669)",
    },
  ];

  const toolKpis = [
    {
      label: "Total Tools",
      value: toolStats.total,
      icon: faToolbox,
      grad: "linear-gradient(135deg,#0ea5e9,#2563eb)",
    },
    {
      label: "Butuh Diperbaiki",
      value: toolStats.pending + toolStats.ng,
      icon: faTriangleExclamation,
      grad: "linear-gradient(135deg,#ef4444,#b91c1c)",
    },
    {
      label: "Sedang Perbaikan",
      value: toolStats.underMaintenance,
      icon: faScrewdriverWrench,
      grad: "linear-gradient(135deg,#f59e0b,#d97706)",
    },
    {
      label: "Selesai Diperbaiki (Hari Ini)",
      value: toolSelesaiHariIni,
      icon: faCircleCheck,
      grad: "linear-gradient(135deg,#10b981,#059669)",
    },
  ];

  if (loading)
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <div className="mt-2 text-muted">Memuat data...</div>
      </div>
    );

  return (
    <div className="admin-overview p-3" style={{ marginTop: "-20px" }}>
      {/* HEADER */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <FontAwesomeIcon icon={faGaugeHigh} className="ov-title-icon" />
        <div>
          <h3 className="mb-0 fw-bold">Dashboard Admin</h3>
          <small className="text-muted">
            Ringkasan status mesin &amp; tools secara real-time
          </small>
        </div>
      </div>

      {/* KPI CARDS */}
      <Row className="g-3 mb-1">
        {kpis.map((k) => (
          <Col xs={12} sm={6} lg={3} key={k.label}>
            <div className="kpi-card" style={{ background: k.grad }}>
              <div className="kpi-icon">
                <FontAwesomeIcon icon={k.icon} />
              </div>
              <div className="kpi-meta">
                <div className="kpi-value">{k.value}</div>
                <div className="kpi-label">{k.label}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* DETAIL STATUS MESIN */}
      <h5 className="section-title mt-4 mb-2">
        <FontAwesomeIcon icon={faIndustry} /> Status Mesin
      </h5>
      <Row className="g-3">
        {/* RUSAK */}
        <Col xs={12} lg={4}>
          <Card className="status-card border-0 shadow-sm h-100">
            <Card.Header className="status-head bg-danger-soft">
              <span>
                <FontAwesomeIcon icon={faTriangleExclamation} /> Mesin Rusak
              </span>
              <Badge bg="danger" pill>
                {rusak.length}
              </Badge>
            </Card.Header>
            <Card.Body className="status-body">
              {rusak.length === 0 ? (
                <EmptyState text="Tidak ada mesin rusak" />
              ) : (
                rusak.map((m) => (
                  <MachineRow key={m.id || m._id} m={m} accent="#ef4444" />
                ))
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* SEDANG PERBAIKAN */}
        <Col xs={12} lg={4}>
          <Card className="status-card border-0 shadow-sm h-100">
            <Card.Header className="status-head bg-warning-soft">
              <span>
                <FontAwesomeIcon icon={faScrewdriverWrench} /> Sedang Perbaikan
              </span>
              <Badge bg="warning" text="dark" pill>
                {perbaikan.length}
              </Badge>
            </Card.Header>
            <Card.Body className="status-body">
              {perbaikan.length === 0 ? (
                <EmptyState text="Tidak ada perbaikan berjalan" />
              ) : (
                perbaikan.map((m) => {
                  const d = durasi(m.maintenance_start_time);
                  return (
                    <MachineRow
                      key={m.id || m._id}
                      m={m}
                      accent="#f59e0b"
                      right={
                        d && (
                          <Badge bg="warning" text="dark">
                            <FontAwesomeIcon icon={faClock} /> {d}
                          </Badge>
                        )
                      }
                    />
                  );
                })
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* SUDAH DIPERBAIKI */}
        <Col xs={12} lg={4}>
          <Card className="status-card border-0 shadow-sm h-100">
            <Card.Header className="status-head bg-success-soft">
              <span>
                <FontAwesomeIcon icon={faCircleCheck} /> Sudah Diperbaiki
              </span>
              <Badge bg="success" pill>
                {finishedRecent.length}
              </Badge>
            </Card.Header>
            <Card.Body className="status-body">
              {finishedRecent.length === 0 ? (
                <EmptyState text="Belum ada riwayat perbaikan" />
              ) : (
                finishedRecent.map((h) => (
                  <div className="machine-row" key={h.id} style={{ borderLeftColor: "#10b981" }}>
                    <div className="mr-main">
                      <span className="mr-code">{h.machine_code}</span>
                      <span className="mr-name">{h.machine_name}</span>
                      <small className="text-muted d-block">
                        {h.sector_name || "-"}
                      </small>
                    </div>
                    <div className="text-end">
                      <Badge bg="success">Selesai</Badge>
                      <small className="text-muted d-block mt-1">
                        {fmtDateTime(h.end_time || h.created_at)}
                      </small>
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* KPI TOOLS */}
      <Row className="g-3 mt-3">
        {toolKpis.map((k) => (
          <Col xs={12} sm={6} lg={3} key={`t-${k.label}`}>
            <div className="kpi-card" style={{ background: k.grad }}>
              <div className="kpi-icon">
                <FontAwesomeIcon icon={k.icon} />
              </div>
              <div className="kpi-meta">
                <div className="kpi-value">{k.value}</div>
                <div className="kpi-label">{k.label}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* DETAIL STATUS TOOLS */}
      <h5 className="section-title mt-4 mb-2">
        <FontAwesomeIcon icon={faToolbox} /> Status Tools
      </h5>
      <Row className="g-3">
        {/* BUTUH DIPERBAIKI */}
        <Col xs={12} lg={4}>
          <Card className="status-card border-0 shadow-sm h-100">
            <Card.Header className="status-head bg-danger-soft">
              <span>
                <FontAwesomeIcon icon={faTriangleExclamation} /> Butuh Diperbaiki
              </span>
              <Badge bg="danger" pill>
                {toolsRusak.length}
              </Badge>
            </Card.Header>
            <Card.Body className="status-body">
              {toolsRusak.length === 0 ? (
                <EmptyState text="Tidak ada tools bermasalah" />
              ) : (
                toolsRusak.map((t) => (
                  <ToolRow
                    key={t.id}
                    t={t}
                    accent="#ef4444"
                    right={
                      t.tool_status === "NG" ? (
                        <Badge bg="dark">NG</Badge>
                      ) : (
                        <Badge bg="danger">Rusak</Badge>
                      )
                    }
                  />
                ))
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* SEDANG PERBAIKAN */}
        <Col xs={12} lg={4}>
          <Card className="status-card border-0 shadow-sm h-100">
            <Card.Header className="status-head bg-warning-soft">
              <span>
                <FontAwesomeIcon icon={faScrewdriverWrench} /> Sedang Perbaikan
              </span>
              <Badge bg="warning" text="dark" pill>
                {toolsPerbaikan.length}
              </Badge>
            </Card.Header>
            <Card.Body className="status-body">
              {toolsPerbaikan.length === 0 ? (
                <EmptyState text="Tidak ada perbaikan berjalan" />
              ) : (
                toolsPerbaikan.map((t) => {
                  const d = durasi(t.start_time);
                  return (
                    <ToolRow
                      key={t.id}
                      t={t}
                      accent="#f59e0b"
                      right={
                        d && (
                          <Badge bg="warning" text="dark">
                            <FontAwesomeIcon icon={faClock} /> {d}
                          </Badge>
                        )
                      }
                    />
                  );
                })
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* SUDAH DIPERBAIKI */}
        <Col xs={12} lg={4}>
          <Card className="status-card border-0 shadow-sm h-100">
            <Card.Header className="status-head bg-success-soft">
              <span>
                <FontAwesomeIcon icon={faCircleCheck} /> Sudah Diperbaiki
              </span>
              <Badge bg="success" pill>
                {toolHistoryRecent.length}
              </Badge>
            </Card.Header>
            <Card.Body className="status-body">
              {toolHistoryRecent.length === 0 ? (
                <EmptyState text="Belum ada riwayat perbaikan" />
              ) : (
                toolHistoryRecent.map((h) => (
                  <div
                    className="machine-row"
                    key={h.id}
                    style={{ borderLeftColor: "#10b981" }}
                  >
                    <div className="mr-main">
                      {h.spec && <span className="mr-code">{h.spec}</span>}
                      <span className="mr-name">{h.part_name}</span>
                      <small className="text-muted d-block">
                        {h.dikerjakan_oleh || "-"}
                      </small>
                    </div>
                    <div className="text-end">
                      <Badge bg="success">Selesai</Badge>
                      <small className="text-muted d-block mt-1">
                        {fmtDateTime(h.end_time || h.start_time)}
                      </small>
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* RINGKASAN MESIN & TOOLS */}
      <Row className="g-3 mt-1">
        <Col xs={12} lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="fw-semibold">
              <FontAwesomeIcon icon={faIndustry} /> Rekap Mesin
            </Card.Header>
            <Card.Body>
              <Table className="mb-0 recap-table" hover responsive>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th className="text-center">Jumlah</th>
                    <th className="text-center">Persentase</th>
                  </tr>
                </thead>
                <tbody>
                  <RecapRow dot="#4f46e5" label="Total" val={machines.length} pct="100%" strong />
                  <RecapRow dot="#22c55e" label="Beroperasi Normal" val={normalCount} pct={percent(normalCount, machines.length)} />
                  <RecapRow dot="#f59e0b" label="Sedang Perbaikan" val={perbaikan.length} pct={percent(perbaikan.length, machines.length)} />
                  <RecapRow dot="#ef4444" label="Rusak / Menunggu" val={rusak.length} pct={percent(rusak.length, machines.length)} />
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="fw-semibold">
              <FontAwesomeIcon icon={faToolbox} /> Rekap Tools
            </Card.Header>
            <Card.Body>
              <Table className="mb-0 recap-table" hover responsive>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th className="text-center">Jumlah</th>
                    <th className="text-center">Persentase</th>
                  </tr>
                </thead>
                <tbody>
                  <RecapRow dot="#4f46e5" label="Total" val={toolStats.total} pct="100%" strong />
                  <RecapRow dot="#22c55e" label="Beroperasi Normal" val={toolStats.normal} pct={percent(toolStats.normal, toolStats.total)} />
                  <RecapRow dot="#f59e0b" label="Sedang Diperbaiki" val={toolStats.underMaintenance} pct={percent(toolStats.underMaintenance, toolStats.total)} />
                  <RecapRow dot="#ef4444" label="Menunggu Perbaikan" val={toolStats.pending} pct={percent(toolStats.pending, toolStats.total)} />
                  <RecapRow dot="#6b7280" label="NG" val={toolStats.ng} pct={percent(toolStats.ng, toolStats.total)} />
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .ov-title-icon {
          font-size: 28px;
          color: #4f46e5;
          background: #eef2ff;
          padding: 10px;
          border-radius: 12px;
        }
        .kpi-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
          border-radius: 16px;
          color: #fff;
          box-shadow: 0 8px 20px rgba(0,0,0,.12);
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .kpi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 28px rgba(0,0,0,.18);
        }
        .kpi-icon {
          font-size: 22px;
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,.22);
          border-radius: 14px;
          flex-shrink: 0;
        }
        .kpi-value { font-size: 30px; font-weight: 800; line-height: 1; }
        .kpi-label { font-size: 13px; opacity: .95; margin-top: 4px; }

        .section-title { font-weight: 700; color: #1e293b; }
        .section-title svg { color: #4f46e5; }

        .status-card { border-radius: 16px; overflow: hidden; }
        .status-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 700;
          border: none;
        }
        .bg-danger-soft { background: #fef2f2; color: #b91c1c; }
        .bg-warning-soft { background: #fffbeb; color: #b45309; }
        .bg-success-soft { background: #ecfdf5; color: #047857; }

        .status-body {
          max-height: 340px;
          overflow-y: auto;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .machine-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          background: #f8fafc;
          border-left: 4px solid #cbd5e1;
          border-radius: 8px;
          padding: 8px 12px;
          transition: background .15s ease;
        }
        .machine-row:hover { background: #eef2f7; }
        .mr-main { min-width: 0; }
        .mr-code {
          display: inline-block;
          font-family: monospace;
          font-weight: 700;
          background: #e2e8f0;
          color: #334155;
          padding: 1px 7px;
          border-radius: 6px;
          margin-right: 6px;
          font-size: 12px;
        }
        .mr-name { font-weight: 600; color: #1e293b; }

        .recap-table th {
          font-size: 12px;
          text-transform: uppercase;
          color: #64748b;
          border-top: none;
        }
        .recap-dot {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin-right: 8px;
        }
      `}</style>
    </div>
  );
};

/* ---------- sub komponen ---------- */
const MachineRow = ({ m, accent, right }) => (
  <div className="machine-row" style={{ borderLeftColor: accent }}>
    <div className="mr-main">
      <span className="mr-code">{m.code}</span>
      <span className="mr-name">{m.name}</span>
      <small className="text-muted d-block">{m.sector?.name || "-"}</small>
    </div>
    {right && <div className="text-end">{right}</div>}
  </div>
);

const ToolRow = ({ t, accent, right }) => (
  <div className="machine-row" style={{ borderLeftColor: accent }}>
    <div className="mr-main">
      {(t.kode || t.spec) && (
        <span className="mr-code">{t.kode || t.spec}</span>
      )}
      <span className="mr-name">{t.part_name}</span>
      <small className="text-muted d-block">{t.sector_name || "-"}</small>
    </div>
    {right && <div className="text-end">{right}</div>}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="text-center text-muted py-4">
    <FontAwesomeIcon icon={faCircleCheck} className="mb-2" style={{ fontSize: 22, opacity: 0.4 }} />
    <div style={{ fontSize: 13 }}>{text}</div>
  </div>
);

const RecapRow = ({ dot, label, val, pct, strong }) => (
  <tr className={strong ? "fw-bold" : ""}>
    <td>
      <span className="recap-dot" style={{ background: dot }} />
      {label}
    </td>
    <td className="text-center">{val}</td>
    <td className="text-center">{pct}</td>
  </tr>
);

export default OverviewTab;
